import { useState, useEffect, useCallback } from 'react'

/**
 * Simple data fetching hook
 * @param {() => Promise<any>} fetcher
 * @param {any[]} deps
 */
export function useQuery(fetcher, deps = []) {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const result = await fetcher()
      setData(result)
    } catch (e) {
      setError(e)
    } finally {
      setLoading(false)
    }
  }, deps)

  useEffect(() => {
    load()
  }, [load])

  return { data, loading, error, refetch: load }
}

/**
 * Mutation hook
 * @param {(args: any) => Promise<any>} mutator
 */
export function useMutation(mutator) {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const mutate = useCallback(
    async (args) => {
      setLoading(true)
      setError(null)
      try {
        const result = await mutator(args)
        return result
      } catch (e) {
        setError(e)
        throw e
      } finally {
        setLoading(false)
      }
    },
    [mutator]
  )

  return { mutate, loading, error }
}
